import React, { FC, useEffect, useMemo, useState } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { deepEqual } from 'fast-equals';
import { Add, Save, Close, Work } from '@mui/icons-material';
// Components
import { Modal, Loader } from '../../../../components';
import {
  Card as CardMui,
  Fade,
  CardActions,
  Button,
  CardContent,
  CardHeader,
  TextField,
  Grid,
  Paper,
  Typography,
  Autocomplete
} from '@mui/material';
// models
import { IDropdownResponse, ISeries } from '../../../../models';
// fetch

import { useSnackbar } from 'notistack';
import { useQuery } from 'react-query';
import {
  getNeighborhoodPlanStyles,
  getNeighborhoodOptionLines,
  getNeighborhoodPlans,
  updateNeighborhoodSeriesGeneralInfo,
  createNeighborhoodSeriesGeneralInfo
} from '../../../../fetch';
import { formatMoney } from '../../../../helpers';

interface ISeriesDetailModal {
  open: boolean;
  modalIsLoading: boolean;
  onClose: () => void;
  currentSeries: ISeries | null;
  fetchSeries: () => void;
  selectedSeriesId?: string | number;
  neighborhoodId: string | number;
}

const SeriesDetailSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  selectedPlanStyles: Yup.array().of(Yup.string()).min(1),
  excludedPlanStyles: Yup.array().of(Yup.number()).notRequired(),
  additionalUpgradeCap: Yup.string(),
  optionLine: Yup.string()
});

export const SeriesDetailModal: FC<ISeriesDetailModal> = ({
  open,
  modalIsLoading,
  onClose,
  currentSeries,
  fetchSeries,
  selectedSeriesId,
  neighborhoodId
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // Selected Plan Styles
  const [selectedPlanStyleValues, setSelectedPlanStyleValues] = useState<IDropdownResponse[]>([]);
  const [inputSelectedPlanStyleValue, setInputSelectedPlanStyleValue] = useState('');

  const { isLoading: isLoadingPlanStylesData, data: planStyles } = useQuery<
    IDropdownResponse[],
    Error
  >(['selected-plan-styles-filters', open], () => getNeighborhoodPlanStyles(selectedSeriesId), {
    onSuccess: d => {
      const options = d.filter(record => currentSeries?.selectedPlanStyles.includes(record.value));
      setSelectedPlanStyleValues(options);
    },
    enabled: open
  });
  const planStylesData = useMemo(() => planStyles ?? [], [planStyles]);

  // Selected Plans
  const [selectedPlanValues, setSelectedPlanValues] = useState<IDropdownResponse[]>([]);
  const [inputSelectedPlanValue, setInputSelectedPlanValue] = useState('');

  const { isLoading: isLoadingPlansData, data: plans } = useQuery<IDropdownResponse[], Error>(
    ['selected-plan-filters', open],
    () => getNeighborhoodPlans(selectedSeriesId),
    {
      onSuccess: d => {
        const options = d.filter(record =>
          currentSeries?.excludedPlanCodes.includes(Number(record.value))
        );
        setSelectedPlanValues(options);
      },
      enabled: open
    }
  );
  const plansData = useMemo(() => plans ?? [], [plans]);

  // Additional Option Cap
  const [actualAdditionalOptionCap, setActualAdditionalOptionCap] = useState<number>(0);

  useEffect(() => {
    if (currentSeries) {
      setActualAdditionalOptionCap(currentSeries.additionalUpgradeCap);
    }
  }, [currentSeries]);

  // Selected Options
  const [optionsValues, setOptionsValues] = useState<IDropdownResponse | null>(null);
  const [inputOptionsValue, setInputOptionsValue] = useState('');

  const { isLoading: isLoadingOptionsData, data: options } = useQuery<IDropdownResponse[], Error>(
    ['selected-options-filters', open],
    () => getNeighborhoodOptionLines(selectedSeriesId),
    {
      onSuccess: d => {
        const options = d.filter(record => currentSeries?.optionLine.includes(record.value));
        setOptionsValues(options[0] ?? { value: 'Any', description: 'Any', shorthand: 'Any' });
      },
      enabled: open
    }
  );
  const optionsData = useMemo(() => options ?? [], [options]);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          name: currentSeries?.name ? currentSeries?.name : '',
          selectedPlanStyles:
            currentSeries?.selectedPlanStyles && currentSeries?.selectedPlanStyles?.length > 0
              ? currentSeries?.selectedPlanStyles
              : [],
          excludedPlanCodes:
            currentSeries?.excludedPlanCodes && currentSeries?.excludedPlanCodes?.length > 0
              ? currentSeries?.excludedPlanCodes
              : [],
          additionalUpgradeCap: currentSeries?.additionalUpgradeCap
            ? formatMoney(currentSeries?.additionalUpgradeCap)
            : formatMoney(0),
          optionLine: currentSeries?.optionLine ? currentSeries?.optionLine : 'Any'
        }}
        validationSchema={SeriesDetailSchema}
        onSubmit={async (values, actions) => {
          const data = {
            ...values,
            additionalUpgradeCap: actualAdditionalOptionCap
          };
          if (currentSeries) {
            try {
              await updateNeighborhoodSeriesGeneralInfo(neighborhoodId, selectedSeriesId, data);
              enqueueSnackbar('Series updated!', {
                variant: 'success'
              });
              onClose();
              fetchSeries();
              actions.resetForm();
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Error updating Series.', {
                variant: 'error'
              });
            }
          } else {
            try {
              await createNeighborhoodSeriesGeneralInfo(neighborhoodId, data);
              enqueueSnackbar('Series created!', {
                variant: 'success'
              });
              onClose();
              fetchSeries();
              actions.resetForm();
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Error creating Series.', {
                variant: 'error'
              });
            }
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    setSelectedPlanStyleValues([]);
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  onClose();
                  setSelectedPlanStyleValues([]);
                  resetForm();
                }
              }}
              maxWidth="md"
            >
              {(isSubmitting || modalIsLoading) && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Typography variant="h5">
                    {currentSeries ? 'EDIT SERIES' : 'ADD NEW SERIES'}
                  </Typography>
                  <div className={classes.content}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <CardMui elevation={0} variant="outlined" className={classes.marginBottom}>
                          <CardHeader
                            avatar={<Work />}
                            title="INFO"
                            className={classes.primaryHeader}
                          />
                          <CardContent className={classes.cardContent}>
                            <Paper elevation={0} square>
                              <div className={classes.column}>
                                <Grid container spacing={1}>
                                  <Grid item xs={12}>
                                    <TextField
                                      required
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Name"
                                      name="name"
                                      value={values.name}
                                      onBlur={handleBlur}
                                      size="small"
                                      onChange={e => setFieldValue('name', e.target.value)}
                                      error={Boolean(touched.name && errors.name)}
                                      helperText={touched.name && errors.name}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Autocomplete
                                      multiple
                                      id="selectedPlanStyles"
                                      value={selectedPlanStyleValues}
                                      onChange={(event, newValue: IDropdownResponse[]) => {
                                        setSelectedPlanStyleValues(newValue);
                                        setFieldValue(
                                          'selectedPlanStyles',
                                          newValue.map(val => val.value)
                                        );
                                      }}
                                      inputValue={inputSelectedPlanStyleValue}
                                      onInputChange={(event, newInputValue) => {
                                        setInputSelectedPlanStyleValue(newInputValue);
                                      }}
                                      options={planStylesData}
                                      getOptionLabel={option => option.description}
                                      filterSelectedOptions
                                      renderOption={(props, option: IDropdownResponse) => {
                                        return (
                                          <li {...props} key={`${option.value}`}>
                                            {option.description}
                                          </li>
                                        );
                                      }}
                                      renderInput={params => (
                                        <TextField
                                          {...params}
                                          disabled={isLoadingPlanStylesData}
                                          label={
                                            isLoadingPlanStylesData
                                              ? 'Loading...'
                                              : 'Selected Plan Styles'
                                          }
                                          placeholder="Selected Plan Styles"
                                        />
                                      )}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Autocomplete
                                      multiple
                                      id="excludedPlans"
                                      value={selectedPlanValues}
                                      onChange={(event, newValue: IDropdownResponse[]) => {
                                        setSelectedPlanValues(newValue);
                                        setFieldValue(
                                          'excludedPlanCodes',
                                          newValue.map(val => Number(val.value))
                                        );
                                      }}
                                      inputValue={inputSelectedPlanValue}
                                      onInputChange={(event, newInputValue) => {
                                        setInputSelectedPlanValue(newInputValue);
                                      }}
                                      options={plansData}
                                      getOptionLabel={option => option.description}
                                      filterSelectedOptions
                                      renderOption={(props, option: IDropdownResponse) => {
                                        return (
                                          <li {...props} key={`${option.value}`}>
                                            {option.description}
                                          </li>
                                        );
                                      }}
                                      renderInput={params => (
                                        <TextField
                                          {...params}
                                          disabled={isLoadingPlansData}
                                          label={
                                            isLoadingPlansData ? 'Loading...' : 'Excluded Plans'
                                          }
                                          placeholder="Excluded Plans"
                                        />
                                      )}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Additional Option Cap"
                                      name="additionalUpgradeCap"
                                      value={values.additionalUpgradeCap}
                                      size="small"
                                      onBlur={e => {
                                        const re = /^[+-]?(\d*\.)?\d+$/;
                                        // Need to strip out all non numeric values from formatted value
                                        const value = e.target.value
                                          .replace('$', '')
                                          .replace(',', '');
                                        // make sure it is number only before we update state
                                        if (value === '' || re.test(value)) {
                                          setActualAdditionalOptionCap(Number(value));
                                        }
                                        setFieldValue(
                                          'additionalUpgradeCap',
                                          formatMoney(e.target.value, 2)
                                        );
                                      }}
                                      onChange={e => {
                                        setFieldValue('additionalUpgradeCap', e.target.value);
                                      }}
                                      error={Boolean(
                                        touched.additionalUpgradeCap && errors.additionalUpgradeCap
                                      )}
                                      helperText={
                                        touched.additionalUpgradeCap && errors.additionalUpgradeCap
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Autocomplete
                                      id="options"
                                      value={optionsValues}
                                      disableClearable
                                      onChange={(event, newValue: IDropdownResponse) => {
                                        setOptionsValues(
                                          newValue ?? {
                                            value: 'Any',
                                            description: 'Any',
                                            shorthand: 'Any'
                                          }
                                        );
                                        setFieldValue('optionLine', newValue?.value ?? 'Any');
                                      }}
                                      inputValue={inputOptionsValue}
                                      onInputChange={(event, newInputValue) => {
                                        setInputOptionsValue(newInputValue);
                                      }}
                                      options={optionsData}
                                      getOptionLabel={option => option.description}
                                      filterSelectedOptions
                                      renderOption={(props, option: IDropdownResponse) => {
                                        return (
                                          <li {...props} key={`${option.value}`}>
                                            {option.description}
                                          </li>
                                        );
                                      }}
                                      renderInput={params => (
                                        <TextField
                                          {...params}
                                          disabled={isLoadingOptionsData}
                                          label={
                                            isLoadingOptionsData ? 'Loading...' : 'Option Line'
                                          }
                                          placeholder="Selected Options"
                                        />
                                      )}
                                    />
                                  </Grid>
                                </Grid>
                              </div>
                            </Paper>
                          </CardContent>
                        </CardMui>
                      </Grid>
                    </Grid>
                  </div>
                  <CardActions>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={currentSeries ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      {currentSeries ? 'Save' : 'Add Series'}
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        if (!deepEqual(initialValues, values)) {
                          const result = window.confirm(
                            'You have unsaved changes, are you sure you want to exit?'
                          );
                          if (result) {
                            resetForm();
                            onClose();
                          } else {
                            return;
                          }
                        } else {
                          onClose();
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </CardActions>
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    '& > div:not(:first-of-type)': {
      marginTop: theme.spacing(1)
    }
  },
  outlinedLabel: {
    backgroundColor: theme.palette.common.white,
    paddingLeft: 2,
    paddingRight: 2
  },
  content: {
    marginTop: theme.spacing(1)
  },
  cardContent: {
    paddingTop: 0
  },
  paperBorder: {
    border: `1px solid ${theme.palette.grey[300]}`
  }
}));
